import { useTable } from "@refinedev/antd";
import { List } from "@refinedev/antd";
import { Alert, Badge, Card, Select, Space, Table, Tag, Typography } from "antd";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useMemo, useState, type FC } from "react";
import type { Deployment } from "../../types";

dayjs.extend(relativeTime);

const { Text } = Typography;

const ENV_COLOR: Record<string, string> = {
  production: "red",
  staging: "orange",
  local: "blue",
};

export const DeploymentList: FC = () => {
  const { tableProps } = useTable<Deployment>({ syncWithLocation: false });
  const [environment, setEnvironment] = useState<string | undefined>();

  const rows = useMemo(() => tableProps.dataSource ?? [], [tableProps.dataSource]);
  const environments = useMemo(
    () => [...new Set(rows.map((d) => d.environmentName).filter((n): n is NonNullable<typeof n> => !!n))],
    [rows],
  );
  const filtered = useMemo(
    () => (environment ? rows.filter((d) => d.environmentName === environment) : rows),
    [rows, environment],
  );
  // Rows arrive newest first, so the first row with a commit per environment is the last one.
  const lastCommits = useMemo(() => {
    const seen = new Map<string, Deployment>();
    for (const d of rows) {
      if (d.environmentName && d.commit && !seen.has(d.environmentName)) seen.set(d.environmentName, d);
    }
    return [...seen.values()];
  }, [rows]);

  return (
    <List title="Deployments" canCreate={false}>
      <Alert
        type="info"
        showIcon
        message="Pro feature (free during alpha)"
        description="Deploy history will be reserved for Pro and Agency plans. Enjoy it for free while Loopress is in alpha."
        style={{ marginBottom: 16 }}
      />
      {lastCommits.length > 0 && (
        <Space data-testid="last-commits" wrap style={{ marginBottom: 16 }}>
          {lastCommits.map((d) => (
            <Card key={d.environmentName} size="small" title="Last commit">
              <Tag color={ENV_COLOR[d.environmentName ?? ""] ?? "default"}>{d.environmentName}</Tag>
              <Text code>{d.commit?.slice(0, 7)}</Text>
              {d.dirty && <Tag color="warning">uncommitted changes</Tag>}
              <Text type="secondary" style={{ fontSize: 12 }}>
                {dayjs(d.pushedAt).fromNow()}
              </Text>
            </Card>
          ))}
        </Space>
      )}
      <Select
        allowClear
        placeholder="Filter by environment"
        aria-label="Filter by environment"
        value={environment}
        onChange={setEnvironment}
        options={environments.map((e) => ({ value: e, label: e }))}
        style={{ width: 220, marginBottom: 16, display: "block" }}
      />
      <Table
        {...tableProps}
        dataSource={filtered}
        rowKey="id"
        locale={{
          emptyText: (
            <div style={{ padding: "32px 0" }}>
              <p style={{ fontWeight: 600, marginBottom: 4 }}>No deploys yet</p>
              <p style={{ color: "#888", margin: 0 }}>
                Runs of <Text code>lps push</Text> will appear here automatically.
              </p>
            </div>
          ),
        }}
      >
        <Table.Column<Deployment>
          title="Project"
          render={(_, record) =>
            record.projectName ?? null
          }
        />
        <Table.Column<Deployment>
          title="Environment"
          render={(_, record) => (
            <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
              {record.environmentName && (
                <Tag color={ENV_COLOR[record.environmentName] ?? "default"} style={{ marginRight: 0 }}>
                  {record.environmentName}
                </Tag>
              )}
              <Text type="secondary" style={{ fontSize: 12 }}>
                {record.url}
              </Text>
            </span>
          )}
        />
        <Table.Column<Deployment>
          title="Status"
          dataIndex="status"
          render={(v: "success" | "failure") =>
            v === "success" ? (
              <Badge status="success" text="Success" />
            ) : (
              <Badge status="error" text="Failed" />
            )
          }
        />
        <Table.Column<Deployment>
          title="Command"
          dataIndex="command"
          render={(v: string) => <Text code>{v}</Text>}
        />
        <Table.Column<Deployment>
          title="Commit"
          render={(_, record) => (
            <Space size={4}>
              {record.commit ? <Text code>{record.commit.slice(0, 7)}</Text> : <Text type="secondary">none</Text>}
              {record.dirty && <Tag color="warning">uncommitted changes</Tag>}
            </Space>
          )}
        />
        <Table.Column<Deployment>
          title="Token"
          dataIndex="tokenLabel"
        />
        <Table.Column<Deployment>
          title="When"
          dataIndex="pushedAt"
          render={(v: string) => dayjs(v).fromNow()}
        />
      </Table>
    </List>
  );
};
